import { ReactComponent as UserIcon } from "assets/images/user.svg";
import { CurrentBrand, TeamPricesData } from "modules/Types";
import { useEffect, useState } from "react";
import { Body2, Subtitle1, Subtitle2 } from "components/shareable/Typography";
import { isValidEmail } from "modules/Utils";
import Banner from "elements/Banner";
import BrandsList from "../BrandsList/BrandsList";
import useScreen from 'Hooks/useScreen';
import { getSimulationPrices_api } from "api/organizationApi"
import { toast } from 'react-toastify'
import { TOAST_SUCCESS_OPTIONS } from '../../Organization_constants'
import { TEAM_DEFAULT_PRICE_PER_SEAT_IN_DOLLARS, TEAM_PLAN_ADDITIONAL_SEAT_PRICE, TEAM_PLAN_INCLUDED_SEATS } from 'modules/Const'
import { Container } from 'modules/Sty';
import MemberPermissionsSelect from '../MemberPermissionsSelect';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import useBrand from 'Hooks/useBrand';
import Colors from "modules/Colors";
import ModalSymphony from "elements/ModalSymphony";
import InputSymphony from "elements/InputSymphony";
import { OrganizationRole, UserPermissionOnBrand } from "modules/Const";
import { toastError } from "modules/Toast";



interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddMemberModal = ({
  isOpen,
  onClose
}: AddMemberModalProps) => {
  const { organization, organizationUsers, inviteOrganizationUsers } = useOrganizationV2();
  const organizationId = organization?.id as number;

  // Get organization-specific pricing data, fallback to constants if not available
  const maxUsersAllowed = organization?.max_users_allowed || TEAM_PLAN_INCLUDED_SEATS;
  const pricePerSeat = organization?.price_per_seat || TEAM_DEFAULT_PRICE_PER_SEAT_IN_DOLLARS;
  
  // When in brand context, ensure the current brand is included in available brands
  const getAvailableBrands = () => {
    const orgBrands = organization?.brands || [];
    const { currentBrand } = useBrand.getState();
    
    if (currentBrand) {
      // Check if current brand is already in organization brands
      const brandExists = orgBrands.some(brand => brand.id === currentBrand.id);
      if (!brandExists) {
        // Add current brand to the list for brand permission assignment
        return [...orgBrands, currentBrand];
      }
    }
    
    return orgBrands;
  };
  
  const organizationBrands = getAvailableBrands();


  const { mobileView } = useScreen();

  const [name, setName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [role, setRole] = useState<OrganizationRole | null>(null)

  // Initialize brands based on role - simplified logic
  const [brandsLocal, setBrandsLocal] = useState<CurrentBrand[]>([])



  const [extraCosts, setExtraCosts] = useState<TeamPricesData | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [currentStep, setCurrentStep] = useState<1 | 2>(1)

  const isStep1Valid = name && lastName && role && email && isValidEmail(email)
  // For members, at least one brand must have a permission other than 'none'
  const isStep2Valid = role !== OrganizationRole.MEMBER || brandsLocal.some(brand => brand.permissionLevel !== UserPermissionOnBrand.NONE)
  const isMissingFields = !isStep1Valid || !isStep2Valid

  // Single useEffect to handle role and organizationBrands changes
  useEffect(() => {
    if (role === OrganizationRole.ADMINISTRATOR) {
      // For admins, pre-select all brands with admin permission
      setBrandsLocal(organizationBrands.map((brand: CurrentBrand): CurrentBrand => ({ 
        ...brand, 
        permissionLevel: UserPermissionOnBrand.ADMINISTRATOR 
      })));
    } else if (role === OrganizationRole.MEMBER) {
      // For members, show all available brands but with no permissions selected (they need to choose)
      setBrandsLocal(organizationBrands.map((brand: CurrentBrand): CurrentBrand => ({ 
        ...brand, 
        permissionLevel: UserPermissionOnBrand.NONE
      })));
    }
  }, [role, organizationBrands])



  const onAddMember = async () => {
    // NOTE: the full form must be filled out before adding a member
    if (!name || !lastName || !email || !role || !isValidEmail(email)) {
      return
    }

    try {
      setIsLoading(true)
      const newMember = {
        name,
        lastName,
        email,
        role: role
      }

      await inviteOrganizationUsers({
        organizationId,
        users: [{
          email,
          role: role,
          firstName: name,
          lastName,
        }],
        brands: brandsLocal.filter(brand => brand.permissionLevel !== UserPermissionOnBrand.NONE) as CurrentBrand[] // Only send brands with actual permissions
      })
      toast.success('New member added to team! Your new teammate will receive an email with the invite.', TOAST_SUCCESS_OPTIONS)
      setName('')
      setLastName('')
      setEmail('')
      setRole(null)
      setBrandsLocal([])
      setCurrentStep(1)
      onClose();
    } catch (error: any) {
      let errorMessage = "Something went wrong adding the member"
      switch (error?.response?.data?.message) {
        case 'User already belongs to an organization':
          errorMessage = "This user already belongs to another Team. Please contact support if you need to add them to this Team."
          break;
        case 'User already invited to your organization':
          errorMessage = "This user has already been invited to your organization. Please contact support if you need help getting them added to the Team."
          break;
      }
      toastError(errorMessage)
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const getExtraCost = async () => {
    const extraCost = await getSimulationPrices_api(organizationId, { typeSimulation: 'add-seat' });
    setExtraCosts(extraCost)
  }

  const getModalProps = () => {
    if (currentStep === 1) {
      return {
        title: "Add a new teammate",
        subtitle: "Enter basic information for your new team member.",
        buttonRightText: "Next" as const,
        onClickButtonRight: handleNext,
        buttonRightDisabled: !isStep1Valid,
        buttonLeftText: undefined,
        onClickButtonLeft: undefined,
        buttonLeftVariant: undefined,
        buttonRightIsLoading: undefined
      }
    } else {
      return {
        title: `Add ${name} ${lastName}`,
        subtitle: "Set brand permissions for this team member.",
        buttonLeftText: "Back" as const,
        onClickButtonLeft: handleBack,
        buttonLeftVariant: "outlined" as const,
        buttonRightText: "Send invite" as const,
        onClickButtonRight: onAddMember,
        buttonRightDisabled: isLoading || !isStep2Valid,
        buttonRightIsLoading: isLoading
      }
    }
  }

  const handleNext = () => {
    if (isStep1Valid) {
      setCurrentStep(2)
    }
  }

  const handleBack = () => {
    setCurrentStep(1)
  }

  const modalProps = getModalProps()

  return (
    <ModalSymphony
      className="addMemberModalComponent"
      isOpen={isOpen}
      fullScreen={mobileView}
      onClose={() => {
        if (isLoading) {
          return
        }
        setCurrentStep(1)
        onClose()
      }}
      title={modalProps.title}
      subtitle={modalProps.subtitle}
      headerIcon={<UserIcon style={{ width: 32, height: 32 }} />}
      maxWidth={500}
      buttonLeftText={modalProps.buttonLeftText}
      onClickButtonLeft={modalProps.onClickButtonLeft}
      buttonLeftVariant={modalProps.buttonLeftVariant}
      buttonRightText={modalProps.buttonRightText}
      onClickButtonRight={modalProps.onClickButtonRight}
      buttonRightDisabled={modalProps.buttonRightDisabled}
      buttonRightIsLoading={modalProps.buttonRightIsLoading}
    >
      {currentStep === 1 && (
        <>
          {/* TODO: in the future we should handle prices based on currency selected */}
          {((organizationUsers?.length || 0) >= maxUsersAllowed && extraCosts) &&
            <Banner className="addMemberModalBanner3" color={Colors.yellowLight} style={{ marginBottom: 16 }}>
              <div className="flex flex-col gap-1">
                <Subtitle1>You're out of seats</Subtitle1>
                <Body2 color={Colors.textColor}>
                  You'll be charged with + ${extraCosts.priceSeatRemaining} on your next invoice and then ${extraCosts.priceSeat} per month for this new user
                </Body2>
              </div>
              <div className="flex gap-2 justify-between">
                <Body2>Extra user prorated {extraCosts.remainingDays} days</Body2>
                <Subtitle2>${extraCosts.priceSeatRemaining}</Subtitle2>
              </div>
            </Banner>
          }
          <Container className="addMemberModalContainer" display='flex' flexDirection='column' gap='16px' width='100%'>
            <div style={{ display: 'flex', gap: 16 }}>
              <InputSymphony
                className="firstNameInput"
                type="text"
                value={name}
                placeholder="First name"
                onChange={(value) => {
                  const REG_EXP: RegExp = /^[ A-Za-z]*$/
                  if (REG_EXP.test(value)) setName(value)
                }}
                style={{ flex: 1 }}
              />
              <InputSymphony
                className="lastNameInput"
                type="text"
                value={lastName}
                placeholder="Last name"
                onChange={(value) => {
                  const REG_EXP: RegExp = /^[ A-Za-z]*$/
                  if (REG_EXP.test(value)) setLastName(value)
                }}
                style={{ flex: 1 }}
              />
            </div>
            <InputSymphony
              className="emailInput"
              type="email"
              value={email}
              placeholder="Email"
              onChange={setEmail}
              error={email?.length && !isValidEmail(email) ? 'Please enter a valid email address' : undefined}
            />
            <MemberPermissionsSelect
              value={role || ''}
              onChange={setRole}
              required
            />
            {role === OrganizationRole.ADMINISTRATOR &&
              <Banner className="addMemberModalBanner1" color={Colors.purpleLighter} style={{ marginBottom: 16 }}>
                As a Team Admin, this teammate will have full access to update profiles, invite new teammates, create campaigns and websites, and update Billing settings.
              </Banner>
            }
          </Container>
        </>
      )}

      {currentStep === 2 && (
        <>

          {role === OrganizationRole.MEMBER && (
            <BrandsList brands={brandsLocal} onUpdateBrands={setBrandsLocal} />
          )}

          {(organizationUsers?.length || 0) + 1 > maxUsersAllowed &&
            <Banner
              className="addMemberModalBanner2"
              style={{ marginTop: 16 }}
              title={`You've used ${maxUsersAllowed} out of ${maxUsersAllowed} included seats in your Team Plan.`}
              description={`Adding this member will add $${pricePerSeat} / month to your subscription.`}
            />
          }
        </>
      )}
    </ModalSymphony>
  );
};

export default AddMemberModal;
